<template>
  <div class="bg-surface-container rounded-large w-full">
    <div v-if="objective.achievedAt" class="mb-1 flex flex-row items-center gap-1 px-4 pt-2">
      <ObjectiveStatusChip :achieved="!!objective.achievedAt" :archived="!!objective.archivedAt" />
      <div class="-mr-2 ml-auto flex shrink-0 flex-row gap-1 self-start">
        <BaseMenu :items="menuItems" v-slot="{ open }">
          <BaseIconButton size="XS" icon="more_vert" :force-pressed="open" />
        </BaseMenu>
      </div>
    </div>

    <div class="px-4 pb-4" :class="[!objective.achievedAt && 'pt-4']">
      <div class="flex min-h-10 flex-row items-center">
        <h4 class="text-title-medium line-clamp-2 text-ellipsis">{{ objective.title }}</h4>
        <div
          v-if="!objective.achievedAt"
          class="-mr-2 ml-auto flex shrink-0 flex-row gap-1 self-start"
        >
          <BaseMenu :items="menuItems" v-slot="{ open }">
            <BaseIconButton size="XS" icon="more_vert" :force-pressed="open" />
          </BaseMenu>
        </div>
      </div>
      <div v-if="feedbackTopic" class="text-on-surface-variant flex flex-row items-center pb-1">
        <BaseIcon :class="feedbackTopic.icon" size="18" class="mr-0.5">{{
          feedbackTopic.icon
        }}</BaseIcon>
        <span class="text-body-small">
          {{ feedbackTopic.title }}
        </span>
      </div>
      <p
        v-if="objective.description"
        class="text-body-medium text-on-surface-variant mt-2 line-clamp-2 text-ellipsis"
      >
        {{ objective.description }}
      </p>
    </div>
  </div>
</template>
<script setup lang="ts">
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import ObjectiveStatusChip from './ObjectiveStatusChip.vue'
  import type { Objective } from '../../composables/use-objectives.ts'
  import type { FeedbackTopic } from '@/core/models/feedback-topic'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { computed } from 'vue'
  import { useUserStore } from '@/stores/user.store'
  import type { SingleMenuItem } from '../base/menu/MenuItem.ts'
  import { DialogPhase } from '@/core/models/dialog/dialog.ts'

  type ObjectiveFeedbackTopicOption = Pick<FeedbackTopic, 'id' | 'title' | 'icon'>

  interface Props {
    objective: Objective
    feedbackTopic?: ObjectiveFeedbackTopicOption
    dialogPhase: DialogPhase
  }

  const props = defineProps<Props>()

  const { t } = useI18n()

  const menuItems = computed(() => {
    const items: SingleMenuItem[] = []

    if (props.dialogPhase === DialogPhase.Feedback) {
      items.push({
        id: 'go-to-objectives-page',
        title: t('objective.goToObjectivesPage'),
        leadingIcon: 'arrow_outward',
        callback: navigateToCoacheeObjectivesPage,
      })
    } else {
      items.push({
        id: 'go-to-objectives-slide',
        title: t('objective.goToObjectivesSlide'),
        leadingIcon: 'arrow_right_alt',
        callback: navigateToCoacheeObjectivesSlide,
      })
    }
    return items
  })
  const router = useRouter()
  const { user } = useUserStore()
  const navigateToCoacheeObjectivesPage = () => {
    if (props.objective.owner.id === user.id) {
      router.push({ name: 'objectives' })
      return
    }
    router.push({
      name: 'coachee',
      params: {
        id: props.objective.owner.id,
      },
    })
  }
  const navigateToCoacheeObjectivesSlide = () => {
    router.push({ name: 'objectives-slide' })
  }
</script>
